import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  AutoAwesome as AiIcon,
  Add as AddIcon,
  Close as ClearIcon,
  Search as SearchIcon,
  MailOutline as MailIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import ContactCard from "../../../components/component-contact/ContactCard";
import { useContactCategories } from "../../../components/component-contact/useContactCategories";
import { apiGetContactsCollection, apiUpdateContact } from "../../../graphQL/addressbook/collections/contacts";
import { toContactCard } from "../../addressbook/presenters";

const PAGE_SIZE = 25;

const sx = {
  empty: { py: 4, display: "flex", justifyContent: "center" },
  cardList: { display: "flex", flexDirection: "column", gap: 1 },
  pagerLabel: { color: "text.secondary", fontSize: 11, minWidth: 72, textAlign: "center" },
};

const EmptyState = ({ message }) => (
  <Box sx={sx.empty}>
    <Typography sx={{ color: "text.secondary", fontSize: 13 }}>{message}</Typography>
  </Box>
);

const toTelHref = (phone) => {
  const raw = String(phone || "").trim();
  if (!raw) return "";
  const cleaned = raw.replace(/[^+\d]/g, "");
  return cleaned ? `tel:${cleaned}` : "";
};

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /Android|iPhone|iPad|iPod|Mobile|IEMobile|Opera Mini/i.test(ua);
};

const PropertyContactListPanel = ({
  property,
  selectedProperties = [],
  authorId,
  onClose,
  onSelect,
  onOpenDetails,
  selectedId,
  onCompose,
  onComposeText,
  onAdd,
  onMagic,
  onBulkEmail,
  onDelete,
  onViewAllProperties,
}) => {
  const activeProperties = useMemo(() => {
    if (Array.isArray(selectedProperties) && selectedProperties.length > 0) return selectedProperties;
    return property?.id ? [property] : [];
  }, [selectedProperties, property]);
  const isMultiProperty = activeProperties.length > 1;
  const primaryProperty = activeProperties[0] || null;
  const hasActiveProps = activeProperties.length > 0;
  const contactLinks = useMemo(
    () =>
      activeProperties.flatMap((p) =>
        Array.isArray(p?.contactList)
          ? p.contactList.map((entry) => ({ ...entry, _propertyId: p?.id, _domain: p?.domain }))
          : []
      ),
    [activeProperties]
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [contacts, setContacts] = useState([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrPhone, setQrPhone] = useState("");

  const contactCategories = useContactCategories(authorId);

  useEffect(() => {
    setPage(1);
    setSearchInput("");
    setSearchFilter("");
  }, [activeProperties.map((p) => p?.id).filter(Boolean).join(",")]);

  useEffect(() => {
    let cancelled = false;
    if (!hasActiveProps || contactLinks.length === 0) {
      setContacts([]);
      return undefined;
    }
    (async () => {
      setLoading(true);
      setError("");
      try {
        const wantedIds = contactLinks.map((e) => e?.contactId).filter(Boolean);
        const roleByContactId = new Map();
        contactLinks.forEach((e) => {
          if (e?.contactId) roleByContactId.set(e.contactId, e?.role || "");
        });

        if (wantedIds.length === 0) {
          setContacts([]);
          return;
        }

        const { items } = await apiGetContactsCollection({
          pagination: { page: 1, limit: wantedIds.length },
          ordering: { field: "updatedAt", order: "Desc" },
          filters: { ids: wantedIds },
        });
        if (cancelled) return;
        const matched = (items || []).map((c) => ({ contact: c, role: roleByContactId.get(c.id) || "" }));
        setContacts(matched);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load property contacts:", err);
          setError("Could not load contacts for this property.");
          setContacts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [hasActiveProps, contactLinks, activeProperties, authorId]);

  const cards = useMemo(
    () => contacts.map(({ contact, role }) => ({ ...toContactCard(contact), role, _raw: contact })),
    [contacts]
  );

  const filtered = useMemo(() => {
    const q = searchFilter.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) =>
      [c.firstName, c.lastName, c.company, c.title, c.email, c.phone, c.role]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [cards, searchFilter]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const handleSearchSubmit = () => {
    setSearchFilter(searchInput.trim());
    setPage(1);
  };
  const handleSearchChange = (v) => {
    setSearchInput(v);
    if (!v.trim()) { setSearchFilter(""); setPage(1); }
  };
  const handlePhoneClick = (phone) => {
    const telHref = toTelHref(phone);
    if (!telHref) return;
    if (isMobileDevice()) {
      window.location.href = telHref;
      return;
    }
    setQrPhone(String(phone || ""));
    setQrOpen(true);
  };

  const qrValue = toTelHref(qrPhone);
  const qrImageUrl = qrValue
    ? `https://quickchart.io/qr?size=220&text=${encodeURIComponent(qrValue)}`
    : "";

  const handleContactCategoryChange = async (contact, category) => {
    if (!contact?.id || !category) return;
    const value = String(category.id || category.value || category.name || "");
    try {
      await apiUpdateContact({ id: contact.id, category: value });
      setContacts((prev) =>
        prev.map((row) =>
          row?.contact?.id === contact.id
            ? { ...row, contact: { ...row.contact, category: value } }
            : row
        )
      );
    } catch (err) {
      console.error("Failed to update contact category:", err);
    }
  };

  return (
    <AddressbookPanelShell
      onClose={onClose}
      scrollResetKey={page}
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              setSearchOpen((v) => !v);
              if (searchOpen && searchInput) handleSearchChange("");
            }}
            sx={{ p: 0.5 }}
          >
            <SearchIcon sx={{ fontSize: 18, color: searchOpen ? "#3B82F6" : "text.secondary" }} />
          </IconButton>
          {searchOpen && (
            <TextField
              autoFocus
              size="small"
              placeholder="Search…"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchSubmit(); }}
              InputProps={{
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleSearchChange("")} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  height: 26, fontSize: 12, borderRadius: 1,
                  "& fieldset": { borderColor: "#D1D5DB" },
                  "&:hover fieldset": { borderColor: "#9CA3AF" },
                  "&.Mui-focused fieldset": { borderColor: "#3B82F6", borderWidth: 1.5 },
                },
              }}
            />
          )}
        </Box>
      }
      headerActions={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => onAdd?.(primaryProperty)}
            title={isMultiProperty ? "Disabled when multiple properties are selected" : "Add contact"}
            disabled={isMultiProperty || !primaryProperty}
            sx={{
              width: 24, height: 24,
              backgroundColor: "#16a34a", color: "#FFFFFF",
              "&:hover": { backgroundColor: "#15803d" },
              "&.Mui-disabled": { backgroundColor: "#A3A3A3", color: "#E5E7EB" },
            }}
          >
            <AddIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onBulkEmail?.(pageItems)}
            title={pageItems.length === 0 ? "No contacts to email" : "Bulk email these contacts"}
            disabled={pageItems.length === 0}
            sx={{
              width: 24, height: 24,
              backgroundColor: "#2563EB", color: "#FFFFFF",
              "&:hover": { backgroundColor: "#1D4ED8" },
              "&.Mui-disabled": { backgroundColor: "#A3A3A3", color: "#E5E7EB" },
            }}
          >
            <MailIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onMagic?.(property)}
            title="Magic"
            sx={{
              width: 24, height: 24,
              backgroundColor: "#7c3aed", color: "#FFFFFF",
              "&:hover": { backgroundColor: "#6d28d9" },
            }}
          >
            <AiIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      }
      footerLeft={
        <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
          {pageItems.length} / {total} property contact{total !== 1 ? "s" : ""}
        </Typography>
      }
      footerRight={
        <>
          <IconButton size="small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
            <ChevronLeft fontSize="small" />
          </IconButton>
          <Typography sx={sx.pagerLabel}>{page} / {totalPages}</Typography>
          <IconButton size="small" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page">
            <ChevronRight fontSize="small" />
          </IconButton>
        </>
      }
    >
      {!hasActiveProps && (
        <EmptyState message="Select a property to view its contacts." />
      )}

      {hasActiveProps && loading && (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center", alignItems: "center", gap: 1.25 }}>
          <CircularProgress size={20} />
          <Typography sx={{ color: "text.secondary", fontSize: 13 }}>Loading contacts...</Typography>
        </Box>
      )}

      {hasActiveProps && !loading && error && <EmptyState message={error} />}

      {hasActiveProps && !loading && !error && contactLinks.length === 0 && (
        <EmptyState message={isMultiProperty ? "No contacts linked to the selected properties yet." : "No contacts linked to this property yet."} />
      )}

      {hasActiveProps && !loading && !error && contactLinks.length > 0 && filtered.length === 0 && (
        <EmptyState message="No contacts match the current search." />
      )}

      {hasActiveProps && !loading && !error && pageItems.length > 0 && (
        <Box sx={sx.cardList}>
          {pageItems.map((card) => (
            <ContactCard
              key={card.id}
              contact={card}
              role={card.role}
              selected={selectedId === card.id}
              showAddress
              categories={contactCategories}
              onClick={() => onSelect?.(card._raw || card)}
              onDoubleClick={() => onOpenDetails?.(card._raw || card)}
              onPhoneClick={handlePhoneClick}
              onCompose={onCompose ? () => onCompose(card._raw || card) : undefined}
              onComposeText={onComposeText ? () => onComposeText(card._raw || card) : undefined}
              onCategoryChange={handleContactCategoryChange}
              onDelete={onDelete ? () => onDelete(card._raw || card) : undefined}
              onViewLinkedProperties={onViewAllProperties ? () => onViewAllProperties(card._raw || card) : undefined}
            />
          ))}
        </Box>
      )}

      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 2, textAlign: "center", position: "relative" }}>
          <IconButton
            size="small"
            onClick={() => setQrOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8, color: "#64748B" }}
            title="Close"
          >
            <ClearIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>Call From Your Phone</Typography>
          {qrImageUrl && (
            <Box
              component="img"
              src={qrImageUrl}
              alt="Phone call QR code"
              sx={{ width: 220, height: 220, mx: "auto", display: "block", borderRadius: 1, border: "1px solid #E2E8F0" }}
            />
          )}
          <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>{qrPhone}</Typography>
        </Box>
      </Dialog>
    </AddressbookPanelShell>
  );
};

export default PropertyContactListPanel;
